/**
 * Updates a list item in the backend data state.
 * @param backendData Current backend data state
 * @param setBackendData Setter function for the backend data state
 * @param updated_item Data of the updated item
 */
export const updateListItemState = (
  backendData,
  setBackendData,
  updated_item
) => {
  const items = backendData?.data?.data || [];
  const item_exists = items.some((item) => item.id === updated_item.id);
  const updatedBackendData = {
    ...backendData,
    data: {
      ...backendData?.data,
      data: item_exists
        ? items.map((item) =>
            item.id === updated_item.id ? { ...item, ...updated_item } : item
          )
        : [updated_item, ...items],
    },
  };
  setBackendData(updatedBackendData);
};

/**
 * Updates an item in multiple state locations simultaneously.
 * Handles both { data: [], meta: {} } and plain array structures.
 * @param backendData Current backend data state
 * @param setBackendData Setter function for the backend data state
 * @param updated_item Data of the updated item
 * @param statePaths Array of paths where the item should be updated
 *                   Each path can be:
 *                   - A string for top-level keys (e.g., 'substitution-list')
 *                   - An array path for nested keys (e.g., ['relations', 'kommunikation'])
 */
export const updateMultiListItemState = (
  backendData,
  setBackendData,
  updated_item,
  statePaths
) => {
  // Use functional update to avoid stale closure issues
  setBackendData(prev => {
    // Start from a shallow copy of the root
    let nextRoot = prev ? { ...prev } : {};

    statePaths.forEach(path => {
      const pathArray = Array.isArray(path) ? path : [path];

      // Clone each level along the path to avoid mutating frozen state
      let current = nextRoot;
      for (let i = 0; i < pathArray.length - 1; i++) {
        const key = pathArray[i];
        const existing = current[key];
        current[key] = existing ? { ...existing } : {};
        current = current[key];
      }

      const finalKey = pathArray[pathArray.length - 1];
      const targetState = current[finalKey];
      if (!targetState) {
        return;
      }

      // Handle different data structures
      let items;
      let updateStructure;

      if (targetState.data && Array.isArray(targetState.data)) {
        // Structure: { data: [], meta: {} }
        items = targetState.data;
        updateStructure = (updatedItems) => ({
          ...targetState,
          data: updatedItems,
        });
      } else if (Array.isArray(targetState)) {
        // Structure: plain array
        items = targetState;
        updateStructure = (updatedItems) => updatedItems;
      } else {
        return; // Unknown structure
      }

      const itemExists = items.some((item) => item.id === updated_item.id);
      const updatedItems = itemExists
        ? items.map((item) =>
            item.id === updated_item.id ? { ...item, ...updated_item } : item
          )
        : [updated_item, ...items];

      current[finalKey] = updateStructure(updatedItems);
    });

    return nextRoot;
  });
};

/**
 * Removes an item from multiple state locations simultaneously.
 * @param backendData Current backend data state
 * @param setBackendData Setter function for the backend data state
 * @param itemId ID of the item to remove
 * @param statePaths Array of paths where the item should be removed
 */
export const removeMultiListItemState = (
  backendData,
  setBackendData,
  itemId,
  statePaths
) => {
  // Use functional update to avoid stale closure issues
  setBackendData(prev => {
    let nextRoot = prev ? { ...prev } : {};

    statePaths.forEach(path => {
      const pathArray = Array.isArray(path) ? path : [path];

      // Clone each level along the path to avoid mutating frozen state
      let current = nextRoot;
      for (let i = 0; i < pathArray.length - 1; i++) {
        const key = pathArray[i];
        const existing = current[key];
        current[key] = existing ? { ...existing } : {};
        current = current[key];
      }

      const finalKey = pathArray[pathArray.length - 1];
      const targetState = current[finalKey];
      if (!targetState) {
        return;
      }

      let items;
      let updateStructure;

      if (targetState.data && Array.isArray(targetState.data)) {
        items = targetState.data;
        updateStructure = (updatedItems) => ({
          ...targetState,
          data: updatedItems,
        });
      } else if (Array.isArray(targetState)) {
        items = targetState;
        updateStructure = (updatedItems) => updatedItems;
      } else {
        return;
      }

      const updatedItems = items.filter((item) => item.id !== itemId);
      current[finalKey] = updateStructure(updatedItems);
    });

    return nextRoot;
  });
};
